import type { EnterCgpEventsClient, EnterCgpEventProperties } from '@enter/cgp-events';
import { getConsentSnapshot } from '@/contexts/consent/store';
import { waitForInitialContent } from '@/lib/initial-content';

type CgpModule = Pick<typeof import('@enter/cgp-events'), 'createEnterCgpEventsClient' | 'DEFAULT_DATA_PLANE_URL'>;
type Event =
    | { kind: 'track'; name: string; props?: EnterCgpEventProperties }
    | { kind: 'page'; path: string }
    | { kind: 'identify'; id: string; traits?: EnterCgpEventProperties };
type QueuedEvent = { event: Event; generation: number; time: number };

export interface CgpConfiguration {
    /** 当前环境的公开 write key。 */
    writeKey?: string;
    /** Home 当前 CGP 数据面地址。 */
    dataPlaneUrl?: string;
    /** 只延后 SDK 下载；授权与事件去重仍走既有机制。 */
    beforeLoad?: () => Promise<void>;
}

/** 生产入口在本模块按文档创建一次；服务端构造不读取浏览器或用户状态。 */
export function createCgpAdapter(configuration: CgpConfiguration, load: () => Promise<CgpModule> = () => import('@enter/cgp-events')) {
    const config = { writeKey: configuration.writeKey?.trim() ?? '', dataPlaneUrl: configuration.dataPlaneUrl?.trim() ?? '' };
    let active = false;
    let client: EnterCgpEventsClient | null = null;
    let loading: Promise<void> | null = null;
    let generation = 0;
    let identity: string | null = null;
    let anonymousConfirmed = false;
    let enabled = false;
    let queue: QueuedEvent[] = [];

    const allowed = () => active && Boolean(config.writeKey) && getConsentSnapshot().analyticsAllowed;

    function resetIdentity(): void {
        generation += 1;
        identity = null;
        queue = [];
        try { client?.reset(); }
        catch { console.error('[cgp] identity reset failed'); }
    }

    function send(event: Event): boolean {
        if (!allowed() || !client) return false;
        try {
            if (event.kind === 'page') return client.page({ path: event.path });
            if (event.kind === 'identify') return client.identify(event.id, event.traits);
            return client.track(event.name, event.props);
        } catch {
            console.error('[cgp] event failed');
            return false;
        }
    }

    function flush(): void {
        const pending = queue;
        queue = [];
        for (const item of pending) {
            if (!allowed()) return;
            if (item.generation === generation && Date.now() - item.time <= 60_000) send(item.event);
        }
    }

    function initialize(): Promise<void> {
        if (!allowed()) return Promise.resolve();
        if (client) { flush(); return Promise.resolve(); }
        if (loading) return loading;
        const requestedGeneration = generation;
        const request = (configuration.beforeLoad?.() ?? Promise.resolve())
            .then(() => allowed() && generation === requestedGeneration ? load() : null)
            .then((module) => {
                if (!module || !allowed() || generation !== requestedGeneration) return;
                client = module.createEnterCgpEventsClient({
                    appName: 'enter-home',
                    writeKey: config.writeKey,
                    dataPlaneUrl: config.dataPlaneUrl || module.DEFAULT_DATA_PLANE_URL,
                    enabled: true,
                    maxQueueSize: 100,
                    environment: process.env.NEXT_PUBLIC_DEPLOY_ENV || process.env.NODE_ENV,
                    debug: process.env.NEXT_PUBLIC_DEBUG_MODE === 'true',
                });
                enabled = true;
                // 初始化不自行 reset：恢复身份是否有效由身份桥在 Auth 给出权威结论后决定，
                // 否则每次整页加载都会先清身份，设备身份与归因属性随之丢失。
                if (anonymousConfirmed) clearRestoredIdentity();
                flush();
            })
            .catch(() => console.error('[cgp] initialization failed'))
            .finally(() => {
                if (loading === request) loading = null;
                if (allowed() && generation !== requestedGeneration) void initialize();
            });
        loading = request;
        return request;
    }

    function accept(event: Event): boolean {
        if (!allowed()) return false;
        if (client) return send(event);
        if (queue.length === 100) queue.shift();
        queue.push({ event, generation, time: Date.now() });
        void initialize();
        return true;
    }

    /** 权威匿名结论到达后，才清掉 SDK 从存储恢复的登录身份。 */
    function clearRestoredIdentity(): void {
        if (identity !== null) return;
        try { client?.reset(); }
        catch { console.error('[cgp] identity reset failed'); }
    }

    function syncConsent(): Promise<void> {
        if (!allowed()) {
            generation += 1;
            identity = null;
            queue = [];
            // 公共包 setEnabled(false) 已清其等待与登录身份，不重复 reset。
            try { if (enabled) client?.setEnabled(false); }
            catch { console.error('[cgp] disable failed'); }
            enabled = false;
            return Promise.resolve();
        }
        try { if (client && !enabled) { client.setEnabled(true); enabled = true; } }
        catch { console.error('[cgp] activation failed'); }
        return initialize();
    }

    return {
        start(): Promise<void> { active = true; return syncConsent(); },
        syncConsent,
        track(name: string, props?: EnterCgpEventProperties): boolean {
            return accept({ kind: 'track', name, props: props && { ...props } });
        },
        page(path: string): boolean {
            if (!path.startsWith('/')) return false;
            return accept({ kind: 'page', path: path.split(/[?#]/, 1)[0] });
        },
        identify(id: string, traits?: EnterCgpEventProperties): void {
            if (!allowed() || !id.trim()) return;
            const normalizedId = id.trim();
            if (identity !== null && identity !== normalizedId) resetIdentity();
            identity = normalizedId;
            accept({ kind: 'identify', id: normalizedId, traits: traits && { ...traits } });
        },
        resetIdentity,
        /** 身份桥确认当前访客确实是匿名；只清恢复的登录身份，不动正常匿名 visitor。 */
        confirmAnonymous(): void {
            anonymousConfirmed = true;
            clearRestoredIdentity();
        },
        dispose(): void { active = false; syncConsent(); },
    };
}

export const cgpClient = createCgpAdapter({
    writeKey: process.env.NEXT_PUBLIC_EVENTS_WRITE_KEY,
    dataPlaneUrl: process.env.NEXT_PUBLIC_EVENTS_DATA_PLANE_URL,
    beforeLoad: waitForInitialContent,
});
